// Request Interceptor - Adds authentication, security headers, and logging

import {
  SECURITY_HEADERS,
  sanitizeForLogging,
  isValidUrl,
} from "../config/security.config";

let tokenGetter = null;
const AUTH_TOKEN_STORAGE_KEY = "axon_auth_token";
const AUTH_TOKEN_EXPIRY_STORAGE_KEY = "axon_auth_token_expires_at";
const OIDC_SCOPES = new Set(["openid", "profile", "email", "offline_access"]);

const decodeJwtPayload = (token) => {
  try {
    const tokenParts = (token || "").split(".");
    if (tokenParts.length !== 3) {
      return null;
    }

    const payload = tokenParts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(tokenParts[1].length / 4) * 4, "=");

    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

const getExpectedAudienceFromScope = () => {
  const configuredScopes = (process.env.REACT_APP_TOKEN_SCOPE || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (configuredScopes.length === 0 || configuredScopes[0] === "t") {
    return "";
  }

  const resourceScope = configuredScopes.find((scope) => {
    const normalizedScope = (scope || "").toLowerCase();
    return normalizedScope.includes("/") && !OIDC_SCOPES.has(normalizedScope);
  });

  if (!resourceScope) {
    return "";
  }

  const slashIndex = resourceScope.lastIndexOf("/");
  return slashIndex > 0
    ? resourceScope.slice(0, slashIndex)
    : resourceScope;
};

const isTokenAudienceValid = (token) => {
  const expectedAudience = getExpectedAudienceFromScope();
  if (!expectedAudience) {
    return true;
  }

  const payload = decodeJwtPayload(token);
  const tokenAudience = payload?.aud;
  const normalizedAudience = Array.isArray(tokenAudience)
    ? tokenAudience[0]
    : tokenAudience;

  return normalizedAudience === expectedAudience;
};

const clearStoredAuthToken = () => {
  localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY);
};

const getStoredAuthToken = () => {
  const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  const expiry = Number(
    localStorage.getItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY) || "0",
  );

  if (!token) {
    return null;
  }

  if (expiry && Date.now() >= expiry) {
    clearStoredAuthToken();
    return null;
  }

  if (!isTokenAudienceValid(token)) {
    console.warn(
      "[Request Interceptor] Stored token audience mismatch. Removing token and skipping Authorization header.",
    );
    clearStoredAuthToken();
    return null;
  }

  return token;
};

// Set the token getter function
export const setTokenGetter = (getter) => {
  if (typeof getter !== "function") {
    console.error("[Request Interceptor] Token getter must be a function");
    return;
  }
  tokenGetter = getter;
};

// Get the current authentication token
const getAuthToken = async () => {
  if (!tokenGetter) {
    // Legacy behavior:
    // return null;
    return getStoredAuthToken();
  }

  try {
    const token = await tokenGetter();
    // Legacy behavior:
    // return token;
    return token || getStoredAuthToken();
  } catch (error) {
    console.error(
      "[Request Interceptor] Error getting auth token:",
      error.message,
    );
    // Legacy behavior:
    // return null;
    return getStoredAuthToken();
  }
};

// Request success interceptor - applied to every outgoing request
export const requestSuccessInterceptor = async (config) => {
  // Check if URL is already absolute
  const isAbsoluteUrl = config.url && /^https?:\/\//i.test(config.url);

  // Validate URL to prevent SSRF attacks
  const fullUrl = isAbsoluteUrl
    ? config.url
    : config.baseURL
      ? `${config.baseURL}${config.url}`
      : config.url;
  if (fullUrl && !isValidUrl(fullUrl)) {
    return Promise.reject(new Error("Invalid or blocked URL"));
  }

  // Add security headers
  config.headers = {
    ...config.headers,
    ...SECURITY_HEADERS,
  };

  // Add authentication token if available
  const token = await getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add request timestamp for performance tracking
  config.metadata = {
    ...config.metadata,
    startTime: new Date().getTime(),
  };

  // Log request in development (sanitized)
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('[API Request]', {
  //     method: config.method?.toUpperCase(),
  //     url: config.url,
  //     params: sanitizeForLogging(config.params),
  //   });
  // }

  return config;
};

// Request error interceptor - handles errors before request is sent
export const requestErrorInterceptor = (error) => {
  console.error("[API Request Error]", {
    message: error.message,
    ...(process.env.NODE_ENV === "development" && { stack: error.stack }),
  });

  return Promise.reject(error);
};

export default {
  setTokenGetter,
  requestSuccessInterceptor,
  requestErrorInterceptor,
};
